import React, { useState } from "react";
import { db } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

const AddCurrencyModal = ({ onAddSuccess }) => {
  const [code, setCode] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cleanCode = code.trim().toUpperCase();
    const cleanRate = parseFloat(exchangeRate);

    // 1. Validation
    if (!cleanCode) {
      setErrorMsg("❌ Currency Code is required.");
      return;
    }
    // Only letters allowed for currency code
    if (!/^[A-Z]{3}$/.test(cleanCode)) {
      setErrorMsg("❌ Currency Code must be exactly 3 letters (e.g., USD, AUD).");
      return;
    }
    if (isNaN(cleanRate) || cleanRate <= 0) {
      setErrorMsg("❌ Rate to GBP must be a number greater than 0.");
      return;
    }

    setErrorMsg("");
    setSuccessMsg("");
    setLoading(true);

    try {
      // 2. Duplicate Check
      const docRef = doc(db, "currencies", cleanCode);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setErrorMsg(`❌ Currency ${cleanCode} is already added.`);
        setLoading(false);
        return;
      }

      // 3. Save to Firestore
      await setDoc(docRef, {
        code: cleanCode,
        exchangeRate: cleanRate,
        enabled: enabled,
        updatedAt: serverTimestamp(),
      });

      setSuccessMsg("✅ Currency added successfully!");
      setCode("");
      setExchangeRate("");
      setEnabled(true);

      // Trigger list refresh
      if (onAddSuccess) {
        onAddSuccess();
      }

      // Automatically close modal after a short delay
      setTimeout(() => {
        setSuccessMsg("");
        const closeBtn = document.querySelector("#addCurrencyModal .btn-close");
        if (closeBtn) {
          closeBtn.click();
        }
      }, 1500);

    } catch (error) {
      console.error("Error adding currency:", error);
      setErrorMsg("❌ Failed to add currency. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="modal fade"
      id="addCurrencyModal"
      tabIndex="-1"
      aria-labelledby="addCurrencyModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content rounded-4 shadow border-0" style={{ backgroundColor: "#F5F7FA" }}>
          <div className="modal-header border-0 pb-0">
            <h5 className="text-h1 fw-bold text-primary w-100 text-center mt-3" id="addCurrencyModalLabel">
              Add New Currency
            </h5>
            <button
              type="button"
              className="btn-close position-absolute end-0 top-0 m-3"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={() => {
                setErrorMsg("");
                setSuccessMsg("");
              }}
            ></button>
          </div>

          <div className="modal-body px-4 pb-4">
            {errorMsg && (
              <div className="alert alert-danger py-2 rounded-3 small" role="alert">
                {errorMsg}
              </div>
            )}
            {successMsg && (
              <div className="alert alert-success py-2 rounded-3 small" role="alert">
                {successMsg}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Currency Code */}
              <div className="mb-3">
                <label htmlFor="currencyCodeInput" className="form-label fw-semibold text-dark">
                  Currency Code
                </label>
                <input
                  id="currencyCodeInput"
                  type="text"
                  className="form-control border-0 shadow-sm py-2"
                  style={{ borderRadius: "8px" }}
                  placeholder="e.g. USD, AUD, EUR"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  maxLength={3}
                  required
                  disabled={loading}
                />
              </div>

              {/* Rate to GBP */}
              <div className="mb-3">
                <label htmlFor="exchangeRateInput" className="form-label fw-semibold text-dark">
                  Rate to GBP
                </label>
                <input
                  id="exchangeRateInput"
                  type="number"
                  step="0.0001"
                  min="0.0001"
                  className="form-control border-0 shadow-sm py-2"
                  style={{ borderRadius: "8px" }}
                  placeholder="e.g. 1.25"
                  value={exchangeRate}
                  onChange={(e) => setExchangeRate(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>

              {/* Enabled Switch */}
              <div className="mb-4">
                <label className="form-label fw-semibold text-dark d-block">Status</label>
                <div className="d-flex align-items-center gap-2">
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="enabledSwitch"
                      checked={enabled}
                      onChange={(e) => setEnabled(e.target.checked)}
                      style={{
                        width: "2.5em",
                        height: "1.25em",
                        cursor: "pointer"
                      }}
                      disabled={loading}
                    />
                  </div>
                  <span className={`fw-semibold ${enabled ? "text-success" : "text-danger"}`}>
                    {enabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="d-grid gap-2">
                <button
                  type="submit"
                  className="btn btn-primary py-2 rounded-3 fw-semibold shadow-sm"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="admin-loader-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Saving...
                    </>
                  ) : (
                    "Save Currency"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCurrencyModal;
